'use client';

import { Checkbox } from '@/components/checkbox/Checkbox';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { hoslogPostQueries } from '@/services/hoslog-plans/hoslog-plans.queries';
import { useMemo } from 'react';
import { z } from 'zod';
import useHoslogPlansMutation from './hooks/useHoslogPlansMutation';

const HoslogPlansForm = ({ planId }: { planId?: string }) => {
  const formSchema = z
    .object({
      planTitle: z.string().min(1, { message: 'common.form.validation.required' }),
      planTitleEng: z.string().min(1, { message: 'common.form.validation.required' }),
      description: z.string().optional(),
      planType: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
      planCategory: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
      planTime: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
      planPrice: z
        .string()
        .min(1, { message: 'common.form.validation.required' })
        .refine((val) => Number(val) >= 0, {
          message: 'common.form.validation.invalid',
        }),
      strikePrice: z.string().optional(),
      isStrikeOut: z.boolean(),
      liquor: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
      nomination: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
      noOfPeople: z.string().refine((value) => value !== '' && value !== 'default', {
        message: 'common.form.validation.required',
      }),
    })
    .superRefine((values, ctx) => {
      if (!values.isStrikeOut) return;
      if (!values.strikePrice || values.strikePrice.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['strikePrice'],
          message: 'common.form.validation.required',
        });
        return;
      }

      if (Number(values.strikePrice) < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['strikePrice'],
          message: 'common.form.validation.invalid',
        });
      }
    });

  const { data, isLoading: isLoadingPlanData } = useQuery({
    ...hoslogPostQueries.details(planId || ''),
    enabled: Boolean(planId),
  });

  const { data: planCategory = [] } = useAttributes('planCategory');
  const { data: planType = [] } = useAttributes('planType');
  const { data: planTime = [] } = useAttributes('planTime');
  const { data: liquorType = [] } = useAttributes('liquor');

  const numberOfPeopleAndNominationOption = [
    {
      label: '1名',
      value: '1',
      labelEng: '1名',
    },
    {
      label: '2名',
      value: '2',
      labelEng: '2名',
    },
    {
      label: '3名',
      value: '3',
      labelEng: '3名',
    },
    {
      label: '4名',
      value: '4',
      labelEng: '4名',
    },
    {
      label: '5名',
      value: '5',
      labelEng: '5名',
    },
    {
      label: '6名',
      value: '6',
      labelEng: '6名',
    },
    {
      label: '7名',
      value: '7',
      labelEng: '7名',
    },
    {
      label: '8名',
      value: '8',
      labelEng: '8名',
    },
    {
      label: '9名',
      value: '9',
      labelEng: '9名',
    },
    {
      label: '10名',
      value: '10',
      labelEng: '10名',
    },
  ];

  const initialValues = useMemo(() => {
    if (!data?.data || !planId) {
      return {
        planTitle: '',
        planTitleEng: '',
        description: '',
        planType: '',
        planCategory: '',
        planTime: '',
        planPrice: '',
        strikePrice: '',
        isStrikeOut: false,
        liquor: '',
        nomination: '',
        noOfPeople: '',
      };
    }

    const planData = data.data;
    return {
      planTitle: planData.planTitle || '',
      planTitleEng: planData.planTitleEng || '',
      description: planData.description || '',
      planPrice: planData.planPrice?.toString() || '0',
      strikePrice: planData.strikePrice?.toString() || '0',
      planType: planType.find((o) => o.value === planData.planType)?.value || '',
      planCategory: planCategory.find((o) => o.value === planData.planCategory)?.value || '',
      planTime: planTime.find((o) => o.value === planData.planTime)?.value || '',
      isStrikeOut: planData.isStrikeOut === 'A',
      liquor: liquorType.find((o) => o.value === planData.liquor)?.value || '',
      nomination: planData.nomination?.toString() || '',
      noOfPeople: planData.noOfPeople?.toString() || '',
    };
  }, [data?.data, planId, planType, planCategory, planTime, liquorType]);

  const { mutateAsync } = useHoslogPlansMutation();
  const { t } = useDynamicLocale();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    values: initialValues,
  });

  const isStrikeOutChecked = form.watch('isStrikeOut');

  return (
    <Form
      form={form}
      onSubmit={async (values) => {
        const { nomination, noOfPeople, strikePrice, isStrikeOut, ...rest } = values;
        await mutateAsync({
          ...rest,
          nomination: Number(nomination),
          noOfPeople: noOfPeople ? Number(noOfPeople) : undefined,
          planId: planId || '',
          strikePrice: strikePrice ?? '0',
          isStrikeOut: isStrikeOut ? 'A' : 'B',
        });
      }}
    >
      <div className="w-full space-y-4.5 max-h-[500px] overflow-y-auto px-0.5 pb-1">
        <div className="grid grid-cols-2 gap-x-4.5">
          <Input
            label="configurationManagement.plans.page.managePlansModal.planTitleJp"
            {...form.register('planTitle')}
            disabled={isLoadingPlanData}
            placeholder={t('configurationManagement.plans.page.placeholder.enterPlanTitleJp')}
            maxLength={50}
            forceRequired
          />

          <Input
            label="configurationManagement.plans.page.managePlansModal.planTitleEng"
            {...form.register('planTitleEng')}
            disabled={isLoadingPlanData}
            placeholder={t('configurationManagement.plans.page.placeholder.enterPlanTitleEng')}
            maxLength={50}
            forceRequired
          />
        </div>

        <Textarea
          label="common.page.tableColumn.description"
          {...form.register('description')}
          disabled={isLoadingPlanData}
          placeholder={t('common.page.tableColumn.description')}
          maxLength={200}
          className="resize-none"
        />

        <div className="grid grid-cols-2 gap-x-4.5">
          <Select
            key={`planType-${planId || 'new'}`}
            {...form.register('planType')}
            options={planType}
            placeholder={t('configurationManagement.plans.page.placeholder.selectPlanType')}
            label="configurationManagement.plans.page.managePlansModal.planType"
            disabled={isLoadingPlanData}
            forceRequired
          />

          <Select
            key={`planCategory-${planId || 'new'}`}
            {...form.register('planCategory')}
            options={planCategory}
            placeholder={t('configurationManagement.plans.page.placeholder.selectPlanCategory')}
            label="configurationManagement.plans.page.managePlansModal.planCategory"
            disabled={isLoadingPlanData}
            forceRequired
          />
        </div>

        <Select
          key={`planTime-${planId || 'new'}`}
          {...form.register('planTime')}
          options={planTime}
          label="configurationManagement.plans.page.managePlansModal.planTime"
          placeholder={t('configurationManagement.plans.page.placeholder.selectPlanTime')}
          disabled={isLoadingPlanData}
          forceRequired
        />

        <div className="grid grid-cols-2 gap-4.5">
          <div className="relative">
            <Input
              label="configurationManagement.plans.page.managePlansModal.planPrice"
              {...form.register('planPrice')}
              placeholder={t('configurationManagement.plans.page.placeholder.enterPlanPrice')}
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
              disabled={isLoadingPlanData}
              forceRequired
            />
            <span className="absolute right-3 top-7 text-muted-foreground pointer-events-none">円</span>
            <div className="absolute top-0 right-0 flex">
              <Checkbox
                label="configurationManagement.plans.page.managePlansModal.strikePrice"
                id="strikePrice"
                checked={isStrikeOutChecked}
                variant="outline"
                color="var(--destructive)"
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  form.setValue('isStrikeOut', isChecked, { shouldValidate: true });
                  if (!isChecked) {
                    form.clearErrors('strikePrice');
                  }
                }}
                disabled={isLoadingPlanData}
              />
            </div>
          </div>
          <div className="relative">
            <Input
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
              label="configurationManagement.plans.page.managePlansModal.strikePrice"
              {...form.register('strikePrice')}
              placeholder={t('configurationManagement.plans.page.placeholder.enterStrikePrice')}
              disabled={isLoadingPlanData || !isStrikeOutChecked}
            />
            <span className="absolute right-3 top-7 text-muted-foreground pointer-events-none">円</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4.5">
          <Select
            key={`planType-${planId || 'new'}`}
            {...form.register('liquor')}
            options={liquorType}
            label="configurationManagement.plans.page.managePlansModal.liquor"
            placeholder={t('configurationManagement.plans.page.placeholder.selectLiquorLimit')}
            disabled={isLoadingPlanData}
            forceRequired
          />

          <Select
            key={`planCategory-${planId || 'new'}`}
            {...form.register('nomination')}
            placeholder={t('configurationManagement.plans.page.placeholder.selectNomination')}
            options={numberOfPeopleAndNominationOption}
            label="configurationManagement.plans.page.managePlansModal.nomination"
            disabled={isLoadingPlanData}
            forceRequired
          />

          <Select
            key={`planCategory-${planId || 'new'}`}
            {...form.register('noOfPeople')}
            placeholder={t('configurationManagement.plans.page.placeholder.select')}
            options={numberOfPeopleAndNominationOption}
            label="configurationManagement.plans.page.managePlansModal.numOfPeople"
            disabled={isLoadingPlanData}
            forceRequired
          />
        </div>
      </div>
    </Form>
  );
};

export default HoslogPlansForm;
